/**
 * crmFunnel/leadHistory/types.ts
 *
 * All TypeScript interfaces for lead-level CRM cohort analysis.
 * One row = one lead. Never aggregated at source.
 *
 * Designed to accept data from Close CRM API in future.
 * For now, driven by mockLeadHistory.ts
 */

// ─────────────────────────────────────────────
// LEAD RECORD — one row per lead
// ─────────────────────────────────────────────
export interface LeadRecord {
  // Identity
  leadId: string;
  email: string;

  // Attribution
  source: string;
  platform: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  metaCampaignId: string;
  metaCampaignName: string;
  metaAdsetId: string;
  metaAdsetName: string;
  metaAdId: string;
  metaAdName: string;
  creativeType: string;
  country: string;
  age: string;
  gender: string;
  device: string;

  // Lifecycle timestamps (YYYY-MM-DD or null)
  leadCreatedAt: string;
  optinDate: string | null;    // earliest optin date per email from Optin collection
  mqlAt: string | null;
  opportunityAt: string | null;
  closedWonAt: string | null;
  lostAt: string | null;

  // Status / value
  currentStage: LeadStage;
  isMql: boolean;
  isOpportunity: boolean;
  isClosedWon: boolean;
  isLost: boolean;
  revenue: number;
}

export type LeadStage = "New Lead" | "MQL" | "Opportunity" | "Closed Won" | "Lost";

// ─────────────────────────────────────────────
// FILTERS
// ─────────────────────────────────────────────
export interface LeadHistoryFilters {
  dateRange?: { start: string; end: string };  // based on leadCreatedAt
  campaignIds?: string[];
  adsetIds?: string[];
  adIds?: string[];
  creativeTypes?: string[];
  countries?: string[];
  ages?: string[];
  genders?: string[];
  devices?: string[];
  sources?: string[];
  platforms?: string[];
  currentStages?: LeadStage[];
}

// ─────────────────────────────────────────────
// COHORT HEATMAP
// ─────────────────────────────────────────────
export type TimeToCloseBucket = "Day 0" | "Days 1–3" | "Days 4–7" | "Days 8–14" | "Days 15–30" | "30+ Days";

export const TIME_TO_CLOSE_BUCKETS: TimeToCloseBucket[] = [
  "Day 0", "Days 1–3", "Days 4–7", "Days 8–14", "Days 15–30", "30+ Days",
];

export interface CohortHeatmapCell {
  bucket: TimeToCloseBucket;
  revenue: number;
  closedWonDeals: number;
  leadToCloseRate: number;   // closed / cohort total leads
  applications: number;      // leads that had a call taken in this bucket
  applicationRate: number;   // applications / cohort total leads %
}

export interface CohortHeatmapRow {
  cohortWeek: string;        // "YYYY-WW" label
  cohortLabel: string;       // "Week of Jan 6"
  totalLeads: number;
  totalRevenue: number;
  totalClosedWon: number;
  cells: Record<TimeToCloseBucket, CohortHeatmapCell>;
}

// ─────────────────────────────────────────────
// AVERAGE DAYS TO CLOSE BY AUDIENCE
// ─────────────────────────────────────────────
export type AudienceBreakdownDimension =
  | "source"
  | "campaign"
  | "adset"
  | "ad"
  | "creativeType"
  | "country"
  | "age"
  | "gender"
  | "device";

export interface AudienceBreakdownRow {
  label: string;
  leads: number;
  closedWonDeals: number;
  revenue: number;
  averageDaysToClose: number;
  medianDaysToClose: number;
  leadToCloseRate: number;
  revenuePerLead: number;
  applications: number;              // leads with a call taken (mqlAt set)
  averageDaysToApplication: number;  // avg days optin → first call
  applicationRate: number;           // applications / leads %
}

// ─────────────────────────────────────────────
// LEAD AGING DISTRIBUTION
// ─────────────────────────────────────────────
export type AgingBucket = "0–3 days" | "4–7 days" | "8–14 days" | "15–30 days" | "30+ days";
export const AGING_BUCKETS: AgingBucket[] = ["0–3 days", "4–7 days", "8–14 days", "15–30 days", "30+ days"];

export interface AgingBucketRow {
  bucket: AgingBucket;
  "New Lead": number;
  "MQL": number;
  "Opportunity": number;
  "Lost": number;
  total: number;
}

// ─────────────────────────────────────────────
// TIME-TO-STAGE FUNNEL
// ─────────────────────────────────────────────
export interface StageNode {
  stage: string;
  volume: number;
  conversionRateFromPrev: number;  // % of previous stage that reached this
  averageDaysFromPrev: number;     // avg days since previous stage (0 for first)
}

export interface TimeToStageFunnel {
  stages: StageNode[];
  totalLeadToCloseAvgDays: number;
}

// ─────────────────────────────────────────────
// COHORT CONVERSION CURVE
// ─────────────────────────────────────────────
export interface CohortCurvePoint {
  day: number;
  cumulativeCloseRate: number;
  cumulativeRevenue: number;
  cumulativeClosedWon: number;
}

export interface CohortCurveLine {
  cohortWeek: string;
  cohortLabel: string;
  totalLeads: number;
  points: CohortCurvePoint[];
}

// ─────────────────────────────────────────────
// VALIDATION
// ─────────────────────────────────────────────
export interface LeadValidationIssue {
  leadId: string;
  field: string;
  message: string;
  severity: "error" | "warning";
}

// ─────────────────────────────────────────────
// SERVICE OUTPUT
// ─────────────────────────────────────────────
export interface LeadHistoryAnalytics {
  cohortHeatmap: CohortHeatmapRow[];
  audienceBreakdown: AudienceBreakdownRow[];
  agingDistribution: AgingBucketRow[];
  timeToStageFunnel: TimeToStageFunnel;
  cohortCurves: CohortCurveLine[];
  meta: {
    totalLeads: number;
    filteredLeads: number;
    dateRange: { start: string; end: string };
    validationIssues: LeadValidationIssue[];
  };
}
